<template>
  <div class="view-container">
    <HeroSection :imageUrl="$tc('calculateView.hero.image.url')">
      <template v-slot:normal>{{
        $tc("calculateView.hero.heading", 1)
      }}</template>
      <template v-slot:bold>{{
        $tc("calculateView.hero.heading", 2)
      }}</template>
    </HeroSection>
    <div class="content-container">
      <ContentContainer>
        <ChatBox>{{ $t("calculateView.chatBox_INTRO.text") }}</ChatBox>
        <QuestionHeader>{{
          $t("calculateView.selection_TYPE.heading")
        }}</QuestionHeader>
        <AnswerContainer>
          <AnswerOptionImage
            id="fixed"
            name="type"
            value="fixed"
            :label="$t(`calculateView.selection_TYPE.options[0].label`)"
            :imageUrl="$tc('calculateView.selection_TYPE.options[0].imageUrl')"
            @change="changeType"
          ></AnswerOptionImage>
          <AnswerOptionImage
            id="free"
            name="type"
            value="free"
            :label="$t(`calculateView.selection_TYPE.options[1].label`)"
            :imageUrl="$tc('calculateView.selection_TYPE.options[1].imageUrl')"
            @change="changeType"
          />
        </AnswerContainer>
      </ContentContainer>
      <ContentContainer v-if="userInput.type" :is-green="true" id="size">
        <QuestionHeader>{{
          $t(`calculateView.selection_SIZE_${forUserInputType}.heading`)
        }}</QuestionHeader>
        <AnswerContainer>
          <div v-if="userInput.type === 'fixed'" class="text-answer-container">
            <input
              type="radio"
              id="x_small"
              value="x_small"
              v-model="userInput.size"
              @change="scrollTo('#season', 1000)"
            />
            <label class="thumbnail answer-label" for="x_small">
              <span class="answer-label-text">{{
                $t(
                  `calculateView.selection_SIZE_${forUserInputType}.options[0].label`
                )
              }}</span>
            </label>
            <p class="small-paragraph margin-vertical-small">
              {{
                $t(
                  `calculateView.selection_SIZE_${forUserInputType}.options[0].description`
                )
              }}
            </p>
          </div>

          <div class="text-answer-container">
            <input
              type="radio"
              id="small"
              value="small"
              v-model="userInput.size"
              @change="scrollTo('#season', 1000)"
            />
            <label class="thumbnail answer-label" for="small">
              <span class="answer-label-text">{{
                $t(
                  `calculateView.selection_SIZE_${forUserInputType}.options[1].label`
                )
              }}</span>
            </label>
            <p class="small-paragraph margin-vertical-small">
              {{
                $t(
                  `calculateView.selection_SIZE_${forUserInputType}.options[1].description`
                )
              }}
            </p>
          </div>

          <div class="text-answer-container">
            <input
              type="radio"
              id="medium"
              value="medium"
              v-model="userInput.size"
              @change="scrollTo('#season', 1000)"
            />
            <label class="thumbnail answer-label" for="medium">
              <span class="answer-label-text">{{
                $t(
                  `calculateView.selection_SIZE_${forUserInputType}.options[2].label`
                )
              }}</span>
            </label>
            <p class="small-paragraph margin-vertical-small">
              {{
                $t(
                  `calculateView.selection_SIZE_${forUserInputType}.options[2].description`
                )
              }}
            </p>
          </div>

          <div class="text-answer-container">
            <input
              type="radio"
              id="large"
              value="large"
              v-model="userInput.size"
              @change="scrollTo('#season', 1000)"
            />
            <label class="thumbnail answer-label" for="large">
              <span class="answer-label-text">{{
                $t(
                  `calculateView.selection_SIZE_${forUserInputType}.options[3].label`
                )
              }}</span>
            </label>
            <p class="small-paragraph margin-vertical-small">
              {{
                $t(
                  `calculateView.selection_SIZE_${forUserInputType}.options[3].description`
                )
              }}
            </p>
          </div>
        </AnswerContainer>
      </ContentContainer>
      <ContentContainer v-if="userInput.size" id="season">
        <QuestionHeader>{{
          $t(`calculateView.selection_SEASON_${forUserInputType}.heading`)
        }}</QuestionHeader>
        <AnswerContainer>
          <AnswerOptionImage
            id="summer"
            name="season"
            value="summer"
            :label="
              $t(
                `calculateView.selection_SEASON_${forUserInputType}.options[0].label`
              )
            "
            :imageUrl="
              $t(
                `calculateView.selection_SEASON_${forUserInputType}.options[0].imageUrl`
              )
            "
            @change="changeSeason"
          >
            <template v-slot:description>{{
              $t(
                `calculateView.selection_SEASON_${forUserInputType}.options[0].description`
              )
            }}</template>
          </AnswerOptionImage>
          <AnswerOptionImage
            id="spring_autumn"
            name="season"
            value="spring_autumn"
            :label="
              $t(
                `calculateView.selection_SEASON_${forUserInputType}.options[2].label`
              )
            "
            :imageUrl="
              $t(
                `calculateView.selection_SEASON_${forUserInputType}.options[2].imageUrl`
              )
            "
            @change="changeSeason"
          >
            <template v-slot:description>{{
              $t(
                `calculateView.selection_SEASON_${forUserInputType}.options[2].description`
              )
            }}</template>
          </AnswerOptionImage>
          <AnswerOptionImage
            id="winter"
            name="season"
            value="winter"
            :label="
              $t(
                `calculateView.selection_SEASON_${forUserInputType}.options[3].label`
              )
            "
            :imageUrl="
              $t(
                `calculateView.selection_SEASON_${forUserInputType}.options[3].imageUrl`
              )
            "
            @change="changeSeason"
          >
            <template v-slot:description>{{
              $t(
                `calculateView.selection_SEASON_${forUserInputType}.options[3].description`
              )
            }}</template>
          </AnswerOptionImage>
        </AnswerContainer>
        <ChatBox>{{ $t("calculateView.chatBox_SEASON.text") }}</ChatBox>
      </ContentContainer>
      <transition name="fade">
        <ContentContainer
          v-if="userInput.season"
          :is-green="true"
          id="foundation"
        >
          <QuestionHeader>{{
            $t("calculateView.selection_IS_FOUNDATION_AVAILABLE.heading")
          }}</QuestionHeader>
          <AnswerContainer>
            <div class="text-answer-container">
              <input
                type="radio"
                id="foundation_is_available"
                name="isFoundationAvailable"
                :value="true"
                v-model="userInput.isFoundationAvailable"
                @change="scrollTo('#construction-support', 1000)"
              />
              <label
                class="thumbnail answer-label"
                for="foundation_is_available"
              >
                <span class="answer-label-text">{{
                  $t(
                    "calculateView.selection_IS_FOUNDATION_AVAILABLE.options[0].label"
                  )
                }}</span>
              </label>
            </div>

            <div class="text-answer-container">
              <input
                type="radio"
                id="foundation_is_not_available"
                name="isFoundationAvailable"
                :value="false"
                v-model="userInput.isFoundationAvailable"
                @change="scrollTo('#foundation-type', 1000)"
              />
              <label
                class="thumbnail answer-label"
                for="foundation_is_not_available"
              >
                <span class="answer-label-text">{{
                  $t(
                    "calculateView.selection_IS_FOUNDATION_AVAILABLE.options[1].label"
                  )
                }}</span>
              </label>
            </div>
          </AnswerContainer>
        </ContentContainer>
      </transition>
      <transition name="fade">
        <ContentContainer
          v-if="userInput.isFoundationAvailable === false"
          id="foundation-type"
          :is-green="true"
        >
          <QuestionHeader>{{
            $t("calculateView.selection_TYPE_OF_FOUNDATION.heading")
          }}</QuestionHeader>
          <AnswerContainer :column="true">
            <AnswerOptionImage
              :row="true"
              id="decking"
              name="typeOfFoundation"
              value="decking"
              :label="
                $t(
                  'calculateView.selection_TYPE_OF_FOUNDATION.options[0].label'
                )
              "
              :imageUrl="
                $t(
                  'calculateView.selection_TYPE_OF_FOUNDATION.options[0].imageUrl'
                )
              "
              @change="changeTypeOfFoundation"
            >
              <template v-slot:description>{{
                $t(
                  "calculateView.selection_TYPE_OF_FOUNDATION.options[0].description"
                )
              }}</template>
            </AnswerOptionImage>
            <AnswerOptionImage
              :row="true"
              id="decking_with_isolation"
              name="typeOfFoundation"
              value="decking_with_isolation"
              :label="
                $t(
                  'calculateView.selection_TYPE_OF_FOUNDATION.options[1].label'
                )
              "
              :imageUrl="
                $t(
                  'calculateView.selection_TYPE_OF_FOUNDATION.options[1].imageUrl'
                )
              "
              @change="changeTypeOfFoundation"
            >
              <template v-slot:description>{{
                $t(
                  "calculateView.selection_TYPE_OF_FOUNDATION.options[1].description"
                )
              }}</template>
            </AnswerOptionImage>
            <AnswerOptionImage
              :row="true"
              id="concrete"
              name="typeOfFoundation"
              value="concrete"
              :label="
                $t(
                  'calculateView.selection_TYPE_OF_FOUNDATION.options[2].label'
                )
              "
              :imageUrl="
                $t(
                  'calculateView.selection_TYPE_OF_FOUNDATION.options[2].imageUrl'
                )
              "
              @change="changeTypeOfFoundation"
            >
              <template v-slot:description>{{
                $t(
                  "calculateView.selection_TYPE_OF_FOUNDATION.options[2].description"
                )
              }}</template>
            </AnswerOptionImage>
          </AnswerContainer>
          <ChatBox>{{
            $t("calculateView.chatBox_TYPE_OF_FOUNDATION.text")
          }}</ChatBox>
        </ContentContainer>
      </transition>
      <transition name="fade">
        <ContentContainer
          v-if="userInput.isFoundationAvailable || userInput.typeOfFoundation"
          id="construction-support"
        >
          <QuestionHeader>{{
            $t("calculateView.selection_CONSTRUCTION_SUPPORT.heading")
          }}</QuestionHeader>
          <AnswerContainer>
            <div class="text-answer-container">
              <input
                type="radio"
                id="none"
                name="constructionSupport"
                value="none"
                v-model="userInput.constructionSupport"
                @change="scrollTo('#cta_next_view', 1000)"
              />
              <label class="thumbnail answer-label construction" for="none">
                <span class="answer-label-text">{{
                  $t(
                    "calculateView.selection_CONSTRUCTION_SUPPORT.options[0].label"
                  )
                }}</span>
              </label>
            </div>

            <div class="text-answer-container">
              <input
                type="radio"
                id="needs_support"
                name="constructionSupport"
                value="needs_support"
                v-model="userInput.constructionSupport"
                @change="scrollTo('#cta_next_view', 1000)"
              />
              <label
                class="thumbnail answer-label construction"
                for="needs_support"
              >
                <span class="answer-label-text">{{
                  $t(
                    "calculateView.selection_CONSTRUCTION_SUPPORT.options[1].label"
                  )
                }}</span>
              </label>
            </div>
          </AnswerContainer>
          <ChatBox>{{
            $t("calculateView.chatBox_CONSTRUCTION_SUPPORT.text")
          }}</ChatBox>
          <router-link
            v-if="userInput.constructionSupport"
            id="cta_next_view"
            :to="{
              name: 'SummaryView',
              params: { userConfiguration, priceInfo, timeInfo },
            }"
          >
            <CtaButton>
              {{ $t("calculateView.cta") }}
              <span>&#10141;</span>
            </CtaButton>
          </router-link>
        </ContentContainer>
      </transition>
      <PreviewBar
        v-if="priceInfo.total && priceInfo.perMonth"
        :totalCost="priceInfo.total | formatPrice(currencySymbol)"
        :perMonthCost="priceInfo.perMonth | formatPrice(currencySymbol)"
      />
    </div>
  </div>
</template>

<script>
import mapInputToConfiguration from "../helpers/mapInputToConfiguration";
import getPriceInfo from "../helpers/getPriceInfo";
import getTimeInfo from "../helpers/getTimeInfo";
import PreviewBar from "../components/PreviewBar";
import QuestionHeader from "../components/form/QuestionHeader";
import ChatBox from "../components/ChatBox";
import CtaButton from "../components/form/CtaButton";
import HeroSection from "../components/HeroSection";
import AnswerContainer from "../components/form/AnswerContainer";
import AnswerOptionImage from "../components/form/AnswerOptionImage";
import ContentContainer from "../components/ContentContainer";

export default {
  name: "CalculateView",
  components: {
    QuestionHeader,
    ChatBox,
    PreviewBar,
    CtaButton,
    HeroSection,
    AnswerContainer,
    AnswerOptionImage,
    ContentContainer,
  },
  data: function () {
    return {
      userInput: {
        type: null,
        size: null,
        season: null,
        isFoundationAvailable: null,
        typeOfFoundation: null,
        constructionSupport: null,
      },
      userConfiguration: {
        type: null,
        size: null,
        season: null,
        foundation: null,
        constructionSupport: null,
      },
      priceInfo: {
        total: null,
        perMonth: null,
        conservatory: {
          total: null,
        },
        foundation: {
          total: null,
          material: null,
          staff: null,
          taxDeductions: null,
        },
        construction: {
          total: null,
          staff: null,
          taxDeductions: null,
        },
      },
      timeInfo: {
        foundationTime: null,
        constructionTime: null,
        // Meta info (nice to have)
        meta: {
          factorUsed: null,
          constructionSupport: null,
          isFoundationAlreadyCompleted: null,
        },
      },
    };
  },
  computed: {
    forUserInputType: function () {
      return this.userInput.type === "fixed" ? "FOR_FIXED" : "FOR_FREE";
    },
    currencySymbol: function () {
      return this.$i18n.locale === "fi" ? "€" : "kr";
    },
  },
  // TODO: Gör detta konsekvent, undvik att blanda 'methods' och
  // 'watch' för att lyssna på uppdaterad input från användaren?
  methods: {
    scrollTo: function (element, time) {
      this.$nextTick(() => {
        this.$scrollTo(element, time);
      });
    },

    changeType: function (value) {
      this.userInput.type = value;

      /* Also, if new value is 'free'
      and previous user input for size was "x_small",
      force new size selection 'small'. */
      if (value === "free" && this.userInput.size === "x_small") {
        this.userInput.size = "small";
      }
      this.scrollTo("#size", 1000);
    },

    changeSeason: function (value) {
      this.userInput.season = value;
      this.scrollTo("#foundation", 1000);
    },
    changeTypeOfFoundation: function (value) {
      this.userInput.typeOfFoundation = value;
      this.scrollTo("#construction-support", 1000);
    },
    postEstimate: function () {
      this.$emit("postEstimate", {
        userConfiguration: this.userConfiguration,
        priceInfo: this.priceInfo,
        timeInfo: this.timeInfo,
        summaryMail: {
          sendSummaryMail: false,
          summaryMailReceiver: null,
          marketingOptIn: null,
          summaryMailContent: {},
        },
      });
    },
  },
  watch: {
    userInput: {
      handler: function () {
        this.userConfiguration = mapInputToConfiguration(this.userInput);
      },
      deep: true,
    },
    userConfiguration: {
      handler: function () {
        this.priceInfo = getPriceInfo(
          this.userConfiguration,
          this.$i18n.locale
        );
      },
      deep: true,
    },
    priceInfo: {
      handler: function () {
        this.timeInfo = getTimeInfo(this.userConfiguration);
      },
      deep: true,
    },
    timeInfo: {
      handler: function () {
        // If the user did the the first selection (type),
        // but not the second (size)
        // (then user has just started the configuration)
        if (this.userInput.type && !this.userInput.size) {
          this.postEstimate();
          // or if the user completed all selections
        } else if (
          Object.values(this.userConfiguration).every(function (value) {
            return value !== null;
          })
        ) {
          this.postEstimate();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
.thumbnail {
  background: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  background-size: contain;
  background-repeat: no-repeat;
  display: inline-block;
  margin: 10px;
}

.answer-label {
  display: flex;
  justify-content: center;
  align-content: stretch;
  align-items: center;
  margin: 0 auto;
  height: auto;
}

.answer-label-text {
  color: var(--primary-green);
  font-size: 18px;
  font-weight: bold;
  padding: 10px 0px;
}

.text-answer-container {
  flex: 0 0 44%;
  margin: 1% 2%;
  text-align: left;
}

.answer-label.construction {
  height: 64px;
}

/* För att undvika "hoppet" när man går från att inte ha border till att ha border */
.text-answer-container > input[type="radio"] + label {
  border: 2px solid transparent;
  outline: 2px solid var(--primary-green);
  text-align: center;
}

.text-answer-container > input[type="radio"]:checked + label,
input[type="radio"]:hover + label {
  border: 2px solid white;
  outline: 2px solid var(--primary-green);
  background: var(--primary-green);
}

.text-answer-container
  > input[type="radio"]:checked
  + label
  > .answer-label-text,
input[type="radio"]:hover + label > .answer-label-text {
  color: white;
}

.content-container {
  flex: 1;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (min-width: $large) {
  .view-container {
    display: flex;
  }

  .answer-label.construction {
    height: auto;
  }
}
</style>
